"use client"
import { useState } from "react"
import { cn } from "@/lib/utils"

export type VibeAxis = {
  key: string
  label: string
  leftLabel: string
  rightLabel: string
  description: string
}

export const VIBE_AXES: VibeAxis[] = [
  {
    key: "socialOrbit",
    label: "Social Orbit",
    leftLabel: "Solitary",
    rightLabel: "Communal",
    description: "Do you prefer working alone or thrive in a community of artists?",
  },
  {
    key: "creativePace",
    label: "Creative Pace",
    leftLabel: "Reflective",
    rightLabel: "Intensive",
    description: "Do you work at a slow, contemplative pace or thrive under productive intensity?",
  },
  {
    key: "infrastructure",
    label: "Infrastructure",
    leftLabel: "Rustic",
    rightLabel: "Fully Equipped",
    description: "Do you need minimal tools or a fully-equipped professional studio?",
  },
  {
    key: "environment",
    label: "Environment",
    leftLabel: "Urban",
    rightLabel: "Rural / Remote",
    description: "Do you draw energy from cities or from nature and isolation?",
  },
  {
    key: "autonomy",
    label: "Autonomy",
    leftLabel: "Structured",
    rightLabel: "Self-Directed",
    description: "Do you prefer a structured program with workshops or complete freedom?",
  },
]

type VibeSliderProps = {
  axis: VibeAxis
  value: number
  onChange: (value: number) => void
  disabled?: boolean
}

export function VibeSlider({ axis, value, onChange, disabled }: VibeSliderProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-[var(--foreground)]">{axis.label}</label>
        <span className="text-xs text-[var(--muted-foreground)] tabular-nums">{(value * 100).toFixed(0)}%</span>
      </div>
      <p className="text-[11px] text-[var(--muted-foreground)] leading-relaxed">{axis.description}</p>
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-[var(--muted-foreground)] w-20 text-right shrink-0">{axis.leftLabel}</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(value * 100)}
          onChange={(e) => onChange(parseInt(e.target.value) / 100)}
          disabled={disabled}
          className={cn(
            "w-full h-2 rounded-full appearance-none cursor-pointer",
            "bg-[var(--muted)]",
            "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5",
            "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--foreground)]",
            "[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md",
            "[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white",
            "[&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full",
            "[&::-moz-range-thumb]:bg-[var(--foreground)] [&::-moz-range-thumb]:cursor-pointer",
            "[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        />
        <span className="text-[11px] text-[var(--muted-foreground)] w-20 shrink-0">{axis.rightLabel}</span>
      </div>
    </div>
  )
}

type VibeSpectrumFormProps = {
  initialValues?: Record<string, number>
  onSave: (values: Record<string, number>) => Promise<void>
  saving?: boolean
}

export function VibeSpectrumForm({ initialValues, onSave, saving }: VibeSpectrumFormProps) {
  const [values, setValues] = useState<Record<string, number>>(
    initialValues ?? Object.fromEntries(VIBE_AXES.map((a) => [a.key, 0.5]))
  )

  const handleChange = (key: string, value: number) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="space-y-6">
      {VIBE_AXES.map((axis) => (
        <VibeSlider
          key={axis.key}
          axis={axis}
          value={values[axis.key] ?? 0.5}
          onChange={(v) => handleChange(axis.key, v)}
          disabled={saving}
        />
      ))}
      <button
        onClick={() => onSave(values)}
        disabled={saving}
        className={cn(
          "w-full py-3 rounded-lg text-sm font-medium transition-colors",
          "bg-[var(--foreground)] text-white hover:bg-[var(--foreground)]/90",
          saving && "opacity-50 cursor-not-allowed"
        )}
      >
        {saving ? "Saving..." : "Save My Spectrum"}
      </button>
    </div>
  )
}

// Aura match display — shows match percentage between artist and residency
export function AuraMatchBadge({ percentage }: { percentage: number }) {
  const color =
    percentage >= 90 ? "text-emerald-600 bg-emerald-50 border-emerald-200" :
    percentage >= 75 ? "text-green-600 bg-green-50 border-green-200" :
    percentage >= 60 ? "text-lime-600 bg-lime-50 border-lime-200" :
    percentage >= 45 ? "text-yellow-600 bg-yellow-50 border-yellow-200" :
    "text-[var(--muted-foreground)] bg-[var(--muted)] border-[var(--border)]"

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold border rounded-full ${color}`}>
      <span className="tabular-nums">{percentage}%</span>
      <span>Vibe Match</span>
    </span>
  )
}
